import { useDispatch, useSelector } from "react-redux"
import { preCheckItemErrored, preCheckItemErroredAndMoveOn, preCheckItemFinished, preCheckItemStarted, selectStudentPreChecks, preCheckItemClearError, updateTroubleshootData, preCheckEvent } from "./studentPreChecksSlice"

/**
 * Provides an API to communicate with the studentPreChecks flow
 * @param {object} props
 * @param {string} props.itemToCheck - the item to be checked.
 * @returns
 */
export default function useStudentPreChecks({itemToCheck}){

    const { currentCheck, contextStates, settings } = useSelector(selectStudentPreChecks)
    const dispatch = useDispatch()
    const currentStatus = contextStates.checks[itemToCheck]
    const currentError = contextStates.errors[itemToCheck]

    const getCheckData = (checkType = currentCheck) => {
        return contextStates.checkData[checkType];
    }
    
    const getAllCheckData = () => {
        return contextStates.checkData;
    }

    /**
     * To start a check for the current checked item.
     * If async flag is true, then it will advance to the next flag without waiting for the result of the current check.
     * @param {boolean} async - if this check should be done asynchronously in the background.
     */
    const startCheck = (async) => {
        dispatch(preCheckItemStarted({item: itemToCheck, async: async}))
    }

    /**
     * To end a check for the current item.
     */
    const finishCheck = () => {
        dispatch(preCheckItemFinished({item: itemToCheck}))
    }

    /**
     * To report an error for the current item and stay in the current check.
     * This allows for components to implement retries.
     * @param {string} errorMessage
     */
    const failedCheck = (error) => {

        let errorMessage

        if(typeof error === 'string') errorMessage = error
        if(typeof error === 'object') errorMessage = error.message || JSON.stringify(error)

        dispatch(preCheckItemErrored({
            item: itemToCheck, 
            error: errorMessage,
            errorObject: error.cause
        }))
    }

    /**
     * To report an error for the current item and move onto the next item.
     * Only use this method if the failure is not considered as a blocking failure.
     * @param {string} errorMessage
     */
    const failedCheckAndMoveOn = (errorMessage) => {
        dispatch(preCheckItemErroredAndMoveOn({item: itemToCheck, error: errorMessage}))
    }

    /**
     * To retry after a failure, will clear the error and call the check callback function.
     * Flow continues once the check is finished with a pass.
     * @param {function} checkCallback - the check function to run
     */
    const retryWith = (checkCallback) => {
        dispatch(preCheckItemClearError({item: itemToCheck}))
        checkCallback()
    }

    const addTroubleshootData = (data) => {
        dispatch(updateTroubleshootData({
            type: currentCheck,
            data: data
        }))
    }

    const getTroubleshootData = () => {
        return contextStates.troubleshootData[currentCheck]
    }

    const getAllTroubleshootData = () => {
        return contextStates.troubleshootData
    }

    const logEvent = (event, actionType = currentCheck, data = null) => {
        // check required data for logging first, prevent logging before initialization is done
        if(!settings.sessionData) return
        // districtId is required for the logging structure in S3 and Athena
        if(!settings.sessionData.districtId) return
        const eventData = {
            type: `ready_${actionType}`,
            item: itemToCheck,
            event,
        }
        if (data) eventData.data = data; // if data is provided, add it to the event data
        dispatch(preCheckEvent(eventData));
    }

    return {
        isCurrent: currentCheck === itemToCheck,
        currentError,
        currentStatus,
        settings,
        startCheck,
        finishCheck,
        failedCheck,
        failedCheckAndMoveOn,
        retryWith,
        addTroubleshootData,
        getTroubleshootData,
        getAllTroubleshootData,
        getCheckData,
        getAllCheckData,
        logEvent,
    }
}